import serverURL from "api/auth/auth";
import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from "axios";
import toast from "react-hot-toast";

interface ErrorResponse {
  error?: string;
  message?: string;
  detail?: string;
}

// Create an Axios instance
const customAxios: AxiosInstance = axios.create({
  baseURL: serverURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to include Authorization token
customAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem("access_token");
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle response errors globally
customAxios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error: AxiosError<ErrorResponse>) => {
    if (error.response) {
      const config = error.config as
        | (InternalAxiosRequestConfig & { _retriedWithoutAuth?: boolean })
        | undefined;
      // A 401 on a request that carried a Bearer token means the stored
      // token has gone stale (expired, or the account no longer exists) —
      // the request interceptor attaches it to every call, so without
      // cleanup the renter keeps hitting 401s even on public endpoints
      // (locations, fleet, price previews). Drop the dead session and
      // replay the request once without Authorization: public endpoints
      // succeed anonymously, genuinely protected calls fail through to
      // the caller, which can re-authenticate.
      if (
        error.response.status === 401 &&
        config &&
        !config._retriedWithoutAuth &&
        config.headers?.Authorization
      ) {
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        config._retriedWithoutAuth = true;
        delete (config.headers as any).Authorization;
        return customAxios(config);
      }
    } else {
      toast.error("Network error: Please check your connection.");
    }

    return Promise.reject(error);
  }
);

export default customAxios;
